import React, { useEffect, useMemo } from 'react';
import { useScheduleBuilder, WIZARD_STEPS } from './hooks/useScheduleBuilder';
import { getEligibleFormats } from './registry/formatEligibility';
import { getMockParticipants, getMockPoolSize } from './data/mock/mockParticipantsService';
import { createRealParticipantsProvider } from './data/real/services/realParticipantsService';
import TestDataStep from './components/wizard/TestDataStep';
import SelectFormatStep from './components/wizard/SelectFormatStep';
import ConfigureParametersStep from './components/wizard/ConfigureParametersStep';
import ConfigureGroupsStep from './components/wizard/ConfigureGroupsStep';
import GeneratedScheduleStep from './components/wizard/GeneratedScheduleStep';

// The only place in this whole module that decides WHERE participant data
// comes from. useScheduleBuilder and every wizard step only ever see this
// shape — { getParticipants(type, count, playersPerTeam), getMaxParticipants(type) }
// — never which concrete source backed it, or whether it's sync or async
// (mock is sync; the real provider is a real network call — both work the
// same way since useScheduleBuilder always awaits). Declared at module scope
// (not inside the component) so it's a stable reference across renders.
const MOCK_PARTICIPANTS_PROVIDER = {
  getParticipants: getMockParticipants,
  getMaxParticipants: getMockPoolSize,
};

// Scoped, dependency-free entrance animation for cards/panels in this feature.
// Respects prefers-reduced-motion. Injected once per mount, not per-render.
const TF_STYLES = `
@keyframes tf-fade-up { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
.tf-animate-in { animation: tf-fade-up 0.32s ease-out both; }
@media (prefers-reduced-motion: reduce) {
  .tf-animate-in { animation: none; }
}
`;

/**
 * New "Schedule" tab, built fresh under src/tournament_formats — deliberately
 * isolated from the legacy ScheduleTab.jsx / tournamentScheduleGenerator.js
 * stack (frozen per KYN-475) while the tournament-format re-architecture is
 * scoped. Standalone (Explore Formats) always runs on mock data
 * (MOCK_PARTICIPANTS_PROVIDER) — real-tournament mode uses
 * createRealParticipantsProvider (realParticipantsService.js), fetching that
 * tournament's actual enrolled teams/players via tournamentParticipantAPI +
 * teamAPI. This is the one file that chooses between them.
 *
 * A wizard (see useScheduleBuilder's `step`): [Test Data, standalone-only] ->
 * Select Format -> Configure Parameters -> Configure Groups
 * (format-conditional, skipped entirely for formats/counts that don't need
 * it) -> Generated Schedule. This component just routes to whichever step is
 * active — each step is its own file under components/wizard/ rather than
 * one growing screen.
 *
 * Two entry points share this exact component:
 * - A real tournament's Schedule tab (`standalone` unset) — `tournament` +
 *   `participantsSummary` are that tournament's real data, which seeds sport/
 *   participant type/count instead of asking (see useScheduleBuilder's
 *   `tournamentContext`), and the Test Data step is skipped entirely.
 * - ExploreTournamentFormatsPage.jsx (`standalone` true) — no real
 *   tournament, so Test Data collects those same fields by hand first.
 */
const ScheduleBuilderTab = ({ tournament, participantsSummary, standalone = false, onOpenLiveTab, activeTab }) => {
  const participantsProvider = useMemo(
    () => (standalone ? MOCK_PARTICIPANTS_PROVIDER : createRealParticipantsProvider(tournament?.id)),
    [standalone, tournament?.id]
  );
  const builder = useScheduleBuilder(standalone ? 'explore' : tournament?.id || 'preview', {
    standalone,
    tournament,
    participantsSummary,
    participantsProvider,
    onOpenLiveTab,
  });
  const { step, refreshLiveState, isLoadingSaved } = builder;

  // A game completed from a page-level live tab (as opposed to the
  // fullscreen embedded view, which already refreshes itself directly — see
  // GeneratedScheduleStep's handleFullscreenGameComplete) is handled by
  // TournamentDetailPage's own handleGameComplete, which has no reference
  // into this hook's state and can't call refreshLiveState directly. Mirrors
  // ScheduleTab.jsx's own "refetch whenever activeTab becomes this tab again"
  // effect — that's the actual signal this tab is visible again after a game
  // closed, not a `tournament.id` change (same id, new object reference).
  useEffect(() => {
    if (activeTab !== undefined && activeTab !== 'smart_schedule') return;
    refreshLiveState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  // Called once per render, right where the wizard sits — every screen that
  // needs to show a format as enabled/disabled (Format step's cards,
  // Parameters step's stale-selection guard) just reads this map instead of
  // deriving its own check. See formatEligibility.js for the actual rules.
  const eligibleFormats = getEligibleFormats(builder.config.participantCount, {
    sportName: builder.config.sportType,
    playedBy: builder.config.sportPlayedBy,
  });

  // Until the mount-time backend load settles we don't yet know whether this
  // tournament has a saved schedule, and guessing wrong is not symmetric:
  // showing "Choose a format" over an existing schedule invites regenerating
  // it. Hold a neutral placeholder instead — see isLoadingSaved's own comment
  // in useScheduleBuilder for why localStorage alone can't answer this.
  if (isLoadingSaved) {
    return (
      <div className="space-y-5">
        <style>{TF_STYLES}</style>
        <div className="tf-animate-in flex items-center justify-center py-16">
          <div className="flex items-center gap-3 text-sm opacity-70">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            Loading schedule…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <style>{TF_STYLES}</style>

      {step === WIZARD_STEPS.TEST_DATA && (
        <TestDataStep
          config={builder.config}
          updateConfig={builder.updateConfig}
          proceedFromTestData={builder.proceedFromTestData}
          maxParticipants={builder.maxParticipantsAvailable}
        />
      )}

      {step === WIZARD_STEPS.FORMAT && (
        <SelectFormatStep
          format={builder.format}
          selectFormat={builder.selectFormat}
          proceedFromFormat={builder.proceedFromFormat}
          nextLabel={builder.formatStepNextLabel}
          standalone={builder.standalone}
          backToTestData={builder.backToTestData}
          eligibleFormats={eligibleFormats}
          error={builder.error}
          participantCount={builder.config.participantCount}
          sportName={builder.config.sportType}
        />
      )}

      {step === WIZARD_STEPS.PARAMETERS && builder.format && (
        <ConfigureParametersStep
          format={builder.format}
          config={builder.config}
          updateConfig={builder.updateConfig}
          needsGroupsStep={builder.needsGroupsStep}
          backToFormat={builder.backToFormat}
          proceedFromParameters={builder.proceedFromParameters}
          eligibleFormats={eligibleFormats}
          error={builder.error}
        />
      )}

      {step === WIZARD_STEPS.GROUPS && builder.format && (
        <ConfigureGroupsStep
          format={builder.format}
          config={builder.config}
          updateConfig={builder.updateConfig}
          groupAssignments={builder.groupAssignments}
          onMoveParticipant={builder.moveParticipant}
          onRandomizeGroups={builder.randomizeGroups}
          backToParameters={builder.backToParameters}
          generate={builder.generate}
          error={builder.error}
        />
      )}

      {step === WIZARD_STEPS.RESULT && builder.schedule && builder.format && (
        <GeneratedScheduleStep
          tournament={tournament}
          refreshLiveState={builder.refreshLiveState}
          format={builder.format}
          schedule={builder.schedule}
          participantType={builder.config.participantType}
          participantCount={builder.config.participantCount}
          legs={builder.config.legs}
          generate={builder.generate}
          viewMode={builder.viewMode}
          tieFormat={builder.tieFormat}
          tieOverrides={builder.tieOverrides}
          matchOrderOverrides={builder.matchOrderOverrides}
          config={builder.config}
          updateCourts={builder.updateCourts}
          roundOverrides={builder.roundOverrides}
          rowOverrides={builder.rowOverrides}
          filters={builder.filters}
          filterOptions={builder.filterOptions}
          currentUserId={builder.currentUserId}
          searchUsers={builder.searchUsers}
          tournamentMatchSettings={builder.tournamentMatchSettings}
          tournamentVO={builder.tournamentVO}
          canManageThisSchedule={builder.canManageThisSchedule}
          canStartTournament={builder.canStartTournament}
          canCompleteTournament={builder.canCompleteTournament}
          startScoring={builder.startScoring}
          openLiveGame={builder.openLiveGame}
          startTournament={builder.startTournament}
          completeTournament={builder.completeTournament}
          isUpdatingTournamentStatus={builder.isUpdatingTournamentStatus}
          tournamentStatusError={builder.tournamentStatusError}
          updateMatchSchedule={builder.updateMatchSchedule}
          deleteMatchRow={builder.deleteMatchRow}
          assignMatchParticipants={builder.assignMatchParticipants}
          addRound={builder.addRound}
          addMatch={builder.addMatch}
          fetchAllParticipants={builder.fetchAllParticipants}
          canSaveSchedule={builder.canSaveSchedule}
          saveSchedule={builder.saveSchedule}
          isSaving={builder.isSaving}
          saveError={builder.saveError}
          lastSavedAt={builder.lastSavedAt}
          applyFilters={builder.applyFilters}
          clearFilters={builder.clearFilters}
          changeViewMode={builder.changeViewMode}
          changeTieFormat={builder.changeTieFormat}
          applyTieOverrides={builder.applyTieOverrides}
          reorderRow={builder.reorderRow}
          applyRoundOverride={builder.applyRoundOverride}
          applyRowOverride={builder.applyRowOverride}
          startEditing={builder.startEditing}
          reset={builder.reset}
        />
      )}
    </div>
  );
};

export default ScheduleBuilderTab;
